<template>
  <div>
    <EvidenceCardListLoading>
      <template #default>
        <div>
          <div v-for="(item, index) in itemsFiltered" :key="index">
            <fade-transition :delay="300">
              <b-row v-if="!item.deleted" class="margin-0 evidence-card">
                <b-col cols="12" class="padding-0 evidence-card__top-box">
                  <div
                    class="evidence-card__caption"
                    v-html="item.caption"
                  ></div>
                  <div class="evidence-card__option-box">
                    <span
                      :class="`chips chips--${generateChipsColor(
                        item.quality
                      )}`"
                    >
                      {{ item.qualityCaption }}
                    </span>
                    <MenuPopupWrapper
                      iconClass="evidence-card__option-icon"
                      menuClass="left-0 top-10 z-index-1 padding-top-24"
                      :items="itemButtons"
                      :permissions="permissions"
                      :showMenu="itemMenuId === item.id"
                      :rowItem="item"
                      @click="showItemMenu"
                      @close="closeItemMenu"
                    />
                  </div>
                </b-col>
                <b-col
                  v-if="item.reference"
                  cols="12"
                  class="evidence-card__reference"
                >
                  {{ `${$lang.evidence_reference}: ` }}
                  <span
                    class="evidence-card__caption"
                    v-html="item.reference"
                  ></span>
                </b-col>
                <b-col cols="12" class="padding-0 evidence-card__bottom-box">
                  <div class="evidence-card__bottom-box-right">
                    <div class="evidence-card__bottom-box-row">
                      <div class="evidence-card__bottom-box-text">
                        <span class="evidence-card__bottom-box-title">
                          {{ $lang.related_exercise }}
                        </span>
                        <span class="evidence-card__bottom-box-caption">
                          {{ item.exerciseCaption }}
                        </span>
                      </div>
                    </div>
                    <div class="evidence-card__bottom-box-row">
                      <div class="evidence-card__bottom-box-text">
                        <span class="evidence-card__bottom-box-title">
                          {{ $lang.related_indicator }}
                        </span>
                        <span class="evidence-card__bottom-box-caption">
                          {{ item.indicatorCaption }}
                        </span>
                      </div>
                      <span class="evidence-card__bottom-box-icon"> </span>
                    </div>
                  </div>
                  <div class="evidence-card__bottom-box-left">
                    <div class="evidence-card__bottom-box-row">
                      <span class="evidence-card__bottom-box-title">
                        {{
                          `${$lang.related_competencies} (${item.allCompetencyCount})`
                        }}
                      </span>
                    </div>
                    <div class="evidence-card__bottom-box-row">
                      <span class="evidence-card__bottom-box-caption">
                        {{ item.flatCompetencies }}
                      </span>
                    </div>
                  </div>
                </b-col>
              </b-row>
            </fade-transition>
          </div>
        </div>
      </template>
    </EvidenceCardListLoading>
    <Modal
      :show="cloneEvidenceModal"
      :title="`${$lang.clone_evidence} ${currentItemId}`"
      :closeOnBackdrop="false"
      :hasClose="false"
      :hasFooter="false"
      size="sm"
      @close="closeCloneEvidenceModal"
    >
      <template #body>
        <CloneEvidence
          :itemId="currentItemId"
          @close="closeCloneEvidenceModal"
        />
      </template>
    </Modal>
    <Modal
      :show="referenceEvidenceModal"
      :title="`${$lang.determine_evidence_reference} ${currentItemId}`"
      :closeOnBackdrop="false"
      :hasClose="false"
      :hasFooter="false"
      size="sm"
      @close="closeReferenceEvidenceModal"
    >
      <template #body>
        <ReferenceEvidence
          :itemId="currentItemId"
          :item="currentItem"
          @getReferenceEvidence="getReferenceEvidence"
          @close="closeReferenceEvidenceModal"
        />
      </template>
    </Modal>
    <Modal
      :show="deleteEvidenceConfirm"
      :title="$lang.delete_evidence"
      :closeOnBackdrop="false"
      :hasClose="false"
      :okButton="true"
      :okButtonText="$lang.delete_evidence"
      okButtonVariant="filled-error"
      size="sm"
      :okLoading="deleteEvidenceLoading"
      :ok="removeEvidence"
      @close="closeDeleteEvidenceConfirm"
    >
      <template #body>
        <div class="margin-bottom-24 font-size-14 color-complementary-300">
          {{ $lang.remove_sure_message.replace("{0}", $lang.evidence) }}
        </div>
      </template>
    </Modal>
    <SamplesListPin
      :visible="toggleSamplesListPin"
      :item="currentItem"
      :title="$lang.evidence_samples"
      @close="closeSamplesListPin"
    />
  </div>
</template>

<script>
import EvidenceCardListLoading from "./EvidenceCardListLoading.vue";
import MenuPopupWrapper from "@/components/re-design/modal/MenuPopupWrapper.vue";
import Modal from "@/components/re-design/modal/Modal.vue";

import CloneEvidence from "./CloneEvidence.vue";
import ReferenceEvidence from "./ReferenceEvidence.vue";
import SamplesListPin from "./samples-list-pin/SamplesListPin.vue";
import { pathOr, includes } from "ramda";
import { goToRoute, storeDispatcher } from "@/shared/utils";
import { deleteEvidence } from "@/modules/kanoon/services";

export default {
  inject: ["listWrapperReload"],
  components: {
    EvidenceCardListLoading,
    MenuPopupWrapper,
    Modal,
    CloneEvidence,
    ReferenceEvidence,
    SamplesListPin,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    permissions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      itemMenuId: 0,
      cloneEvidenceModal: false,
      referenceEvidenceModal: false,
      deleteEvidenceConfirm: false,
      deleteEvidenceLoading: false,
      currentItemId: null,
      currentItem: null,
      deletedItemsId: [],
      toggleSamplesListPin: false,
    };
  },
  computed: {
    itemsFiltered() {
      return this.items.map((item) => {
        const id = pathOr("", ["id"], item);
        const evidence = pathOr("", ["evidence"], item);
        const referenceId = pathOr("", ["reference", "id"], item);
        const referenceEvidence = pathOr("", ["reference", "evidence"], item);
        const captionThirdPerson = pathOr("", ["caption_third_person"], item);
        const competencies = pathOr([], ["competency", "competencies"], item);
        const indicator = pathOr(null, ["indicator"], item);
        const exercise = pathOr(null, ["exercise"], item);
        const deleted = includes(id, this.deletedItemsId);

        return {
          id,
          captionThirdPerson,
          exerciseDesignId: pathOr(null, ["exercise_design_id"], item),
          caption:
            id || evidence
              ? `${id} <span class="color-complementary-100">|</span> ${evidence}`
              : "",
          reference:
            referenceId || referenceEvidence
              ? `${referenceId} <span class="color-complementary-100">|</span> ${referenceEvidence}`
              : "",
          allCompetencyCount: pathOr(
            0,
            ["competency", "all_competency_count"],
            item
          ),
          competencies,
          flatCompetencies: competencies.map((item) => item.caption).join(", "),
          exercise,
          exerciseCaption: pathOr("", ["caption"], exercise),
          indicator,
          indicatorCaption: pathOr("", ["caption"], indicator),
          quality: pathOr(null, ["quality"], item),
          qualityCaption: pathOr(null, ["quality", "caption"], item),
          deleted,
        };
      });
    },
    itemButtons() {
      return [
        {
          caption: this.$lang.edit,
          icon: "pencil-outline",
          show: true,
          permissions: ["admin", "edit"],
          loading: false,
          click: this.goToEditEvidence,
        },
        {
          caption: this.$lang.delete,
          icon: "trash-can-outline",
          show: true,
          permissions: ["admin", "edit"],
          loading: false,
          click: this.showDeleteEvidenceConfirm,
        },
        {
          caption: this.$lang.clone_tooltip,
          icon: "content-copy",
          show: true,
          permissions: ["admin", "edit"],
          loading: false,
          click: this.showCloneEvidenceModal,
        },
        {
          caption: this.$lang.set_reference,
          icon: "feature-search-outline",
          show: true,
          permissions: ["admin", "edit"],
          loading: false,
          click: this.showReferenceEvidenceModal,
        },
        {
          caption: this.$lang.evidence_samples,
          icon: "account-eye-outline",
          show: true,
          permissions: ["admin", "edit"],
          loading: false,
          click: this.showSamplesListPin,
        },
      ];
    },
  },
  methods: {
    pathOr,
    includes,
    goToRoute,
    generateChipsColor(quality) {
      const id = pathOr(1, ["id"], quality);
      const qualityObj = {
        1: "success",
        2: "warning",
        3: "error-light",
      };
      return pathOr("", [id], qualityObj);
    },
    showItemMenu(item) {
      const id = pathOr(null, ["id"], item);
      this.itemMenuId = this.itemMenuId === id ? null : id;
    },
    closeItemMenu() {
      this.itemMenuId = null;
    },
    goToEditEvidence(item) {
      storeDispatcher("mobileHeaderTitleText", this.$lang.modify_evidence);
      storeDispatcher("mobileHeaderBaseRoute", this.$route.name);
      const id = pathOr(null, ["id"], item);
      goToRoute("edit-evidence", { id }, {}, false);
    },
    showCloneEvidenceModal(item) {
      this.currentItemId = pathOr(null, ["id"], item);
      this.cloneEvidenceModal = true;
    },
    closeCloneEvidenceModal() {
      this.currentItemId = null;
      this.cloneEvidenceModal = false;
    },
    showReferenceEvidenceModal(item) {
      this.currentItemId = pathOr(null, ["id"], item);
      this.currentItem = item;
      this.referenceEvidenceModal = true;
    },
    closeReferenceEvidenceModal() {
      this.currentItemId = null;
      this.referenceEvidenceModal = false;
    },
    getReferenceEvidence(item, evidenceSelected) {
      // set selected evidence inside reference evidence of current item
      const referenceId = pathOr("", ["id"], evidenceSelected);
      const referenceEvidence = pathOr("", ["caption"], evidenceSelected);
      const reference =
        referenceId || referenceEvidence
          ? `${referenceId} <span class="color-complementary-100">|</span> ${referenceEvidence}`
          : "";
      item.reference = reference;
    },
    showDeleteEvidenceConfirm(item) {
      this.currentItemId = pathOr(null, ["id"], item);
      this.deleteEvidenceConfirm = true;
    },
    closeDeleteEvidenceConfirm() {
      this.currentItemId = null;
      this.deleteEvidenceConfirm = false;
    },
    async removeEvidence() {
      try {
        this.deleteEvidenceLoading = true;
        const response = await deleteEvidence(this.currentItemId);
        const { message } = response;
        this.$notify.success(message);
        this.deletedItemsId.push(this.currentItemId);
        this.closeDeleteEvidenceConfirm();
        this.listWrapperReload();
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.deleteEvidenceLoading = false;
      }
    },
    showSamplesListPin(item) {
      this.currentItem = item;
      this.toggleSamplesListPin = true;
    },
    closeSamplesListPin() {
      this.toggleSamplesListPin = false;
    },
  },
};
</script>
